import type { ComponentPropsWithRef } from 'react';
import { cn } from '../../lib/cn.ts';

export type HeroBarProps = Omit<ComponentPropsWithRef<'div'>, 'style'>;

/*
 * The navy identity bar on an object detail page — a meeting, a template. It
 * carries the object's name, one meta line, and the actions that are about the
 * object as a whole rather than about what is inside it (on a meeting: joining
 * it, renaming it, deleting it). No avatar: a disc of initials beside a title
 * that spells the same name out is decoration, and it costs the title the width
 * it needs to stay on one line.
 *
 * It *is* the page's heading — there is no plain title above it. What the
 * object can be set to lives under the bar instead, in HeroControlRow.
 *
 * The green wash in the top-right corner is the same light the page background
 * carries, caught on a dark surface. It is drawn with a pseudo-element so it
 * cannot intercept a click meant for the control underneath.
 */
export function HeroBar({ className, children, ...props }: HeroBarProps) {
  return (
    <div
      data-slot='hero-bar'
      className={cn(
        'relative flex flex-wrap items-center gap-3.5 overflow-hidden rounded-lg bg-primary px-5.5 py-3.5 text-white shadow-hero',
        'after:pointer-events-none after:absolute after:-top-17 after:-right-12 after:size-45 after:rounded-full',
        'after:bg-[radial-gradient(circle,var(--color-accent-tint),transparent_64%)]',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export type HeroBodyProps = Omit<ComponentPropsWithRef<'div'>, 'style'>;

export function HeroBody({ className, ...props }: HeroBodyProps) {
  return (
    <div
      data-slot='hero-body'
      className={cn('relative z-10 min-w-0 flex-1', className)}
      {...props}
    />
  );
}

export type HeroTitleProps = Omit<ComponentPropsWithRef<'h1'>, 'style'>;

/*
 * The page's one h1. The bar replaced the plain title that used to stand above
 * it, so the name in the bar is the heading — which is why it is set at the
 * display sizes' little brother rather than at the meta line's size.
 */
export function HeroTitle({ className, ...props }: HeroTitleProps) {
  return (
    <h1
      data-slot='hero-title'
      className={cn('text-xl leading-tight font-semibold tracking-tight', className)}
      {...props}
    />
  );
}

export type HeroMetaProps = Omit<ComponentPropsWithRef<'div'>, 'style'>;

/* When, where, with whom — one line, wrapping rather than truncating. */
export function HeroMeta({ className, ...props }: HeroMetaProps) {
  return (
    <div
      data-slot='hero-meta'
      className={cn(
        'mt-0.5 flex flex-wrap items-center gap-1.5 text-sm text-on-primary-muted',
        className,
      )}
      {...props}
    />
  );
}

export type HeroMetaActionProps = Omit<ComponentPropsWithRef<'button'>, 'style'>;

/*
 * A meta fact that opens something — the rest of a long attendee list. It reads
 * as the line it sits in, so it inherits the meta's size and colour and carries
 * only an underline to say it can be clicked. A Button here would claim to be
 * the bar's control, which HeroActions already holds.
 */
export function HeroMetaAction({
  className,
  type = 'button',
  ...props
}: HeroMetaActionProps) {
  return (
    <button
      data-slot='hero-meta-action'
      type={type}
      className={cn(
        'cursor-pointer rounded-xs underline underline-offset-2 transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-primary-line',
        className,
      )}
      {...props}
    />
  );
}

export type HeroMetaSeparatorProps = Omit<ComponentPropsWithRef<'span'>, 'style'>;

/* A dot between two meta facts. Decoration, so it is hidden from the reading order. */
export function HeroMetaSeparator({ className, ...props }: HeroMetaSeparatorProps) {
  return (
    <span
      data-slot='hero-meta-separator'
      aria-hidden='true'
      className={cn('size-0.75 shrink-0 rounded-full bg-on-primary-faint', className)}
      {...props}
    />
  );
}

export type HeroActionsProps = Omit<ComponentPropsWithRef<'div'>, 'style'>;

/*
 * The object's own actions, pushed to the far edge of the bar — joining the
 * meeting, and the two acts that only its owner has (rename, delete).
 *
 * Actions only. What the object can be *set* to — its gesprekstype, what Wundo
 * may do here — stands under the bar in HeroControlRow, where a setting reads
 * as a setting instead of as a button on a banner.
 *
 * It never shrinks — a squeezed pill reads as a broken one — but it may not
 * grow past the bar either, so `max-w-full` lets the wrap take over where the
 * controls no longer fit on one line beside the title.
 */
export function HeroActions({ className, ...props }: HeroActionsProps) {
  return (
    <div
      data-slot='hero-actions'
      className={cn(
        'relative z-10 flex max-w-full shrink-0 flex-wrap items-center gap-2',
        className,
      )}
      {...props}
    />
  );
}
